import React, { useEffect, useState } from "react";
import { VsiBubble, VsiH4, VsiHr, VsiLgText } from "../../../styled/App.styled";
import {
  useAuthWithRoute,
  useDataWithRoute,
  useDebounce,
  useTitleFromComponent, useUrlState
} from "../../../utils/hooks";
import { useVsiAppContext } from "../../../stores/VsiApp";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  ButtonGroup,
  Col,
  Input,
  Row,
  InputGroup,
  InputGroupText,
  FormGroup,
  FormFeedback,
  Card,
  UncontrolledTooltip,
} from "reactstrap";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import {
  forecastWithRateV2,
  formatStockChartConfig,
  returnDates
} from "../../../utils/Helpers";
import Stockchart from "../../../components/Stockchart";
import Grid from "../../../components/Grid";
import CustomResourceCard from "../../../components/capacityModeling/CustomResourceCard";
import { cloudResourceModelingDefs } from "../../../utils/gridColumnDefs/cloud/cloudResourceModeling";
import dayjs from "dayjs";
import { DeleteAuth, PostAuth, PutAuth } from "../../../utils/Verbs";

const allTypes = [
  {
    name: "cost",
    label: "Cost",
    chartDefs: [
      { name: "Cost per Month", field: "total_cost" },
      { name: "On Prem Cost", field: "on_prem_cost" },
      { name: "Budget", field: "budget" },
    ],
    chartConfig: {
      yAxis: {
        title: {
          text: "$ USD",
        },
      },
    },
  },
  {
    name: "vcpu",
    label: "vCPU",
    chartDefs: [{ name: "Virtual CPU Used", field: "vcpu" }],
    chartConfig: {
      yAxis: {
        title: {
          text: "CPUs",
        },
      },
    },
  },
  {
    name: "disk",
    label: "Disk",
    chartDefs: [{ name: "Disk Used (GiB)", field: "disk_used" }],
    chartConfig: {
      yAxis: {
        title: {
          text: "GiB",
        },
      },
    },
  },
  {
    name: "memory",
    label: "Memory",
    chartDefs: [{ name: "Memory Used (GiB)", field: "memory_used" }],
    chartConfig: {
      yAxis: {
        title: {
          text: "GiB",
        },
      },
    },
  },
  {
    name: "vmbuild",
    label: "VMs",
    chartDefs: [{ name: "# of VMs", field: "total_vms" }],
    chartConfig: {
      yAxis: {
        title: {
          text: "VMs",
        },
      },
    },
  },
];

const BudgetCard = ({
  budget,
  changeBudget,
  isBudgetForecasted,
  changeIsBudgetForecasted,
  allClusterOptions,
  cluster,
  onClusterChange,
  isClustersLoading,
}) => {
  const [activeBudget, setActiveBudget] = useState(budget || 0);
  const debouncedBudget = useDebounce(activeBudget, 500);

  useEffect(() => {
    setActiveBudget(budget || 0);
  }, [budget]);

  useEffect(() => {
    if (debouncedBudget) {
      changeBudget(activeBudget);
    }
  }, [debouncedBudget]);

  return (
    <VsiBubble className={"mt-1"} noAutobot>
      <Row>
        <Col xs={6}>
          <VsiLgText textAlign={"left"}>Budget:</VsiLgText>
          <InputGroup>
            <InputGroupText color={"secondary"}>$</InputGroupText>
            <Input
              name="text"
              type={"number"}
              value={activeBudget}
              onChange={({ target }) => setActiveBudget(target.value)}
            />
          </InputGroup>
        </Col>
        <Col xs={6}>
          <VsiLgText textAlign={"left"}>Forecast Budget?</VsiLgText>
          <FormGroup
            switch
            className={
              "d-flex flex-row justify-content-start align-items-center"
            }
          >
            <Input
              checked={isBudgetForecasted}
              onChange={changeIsBudgetForecasted}
              type="switch"
            />
          </FormGroup>
        </Col>
        <Col xs={12}>
          <VsiLabeledSelect
              isDisabled={isClustersLoading}
              isLoading={isClustersLoading}
              options={allClusterOptions}
              label={"On Prem Cost Source (Virtual Cluster):"}
              onChange={onClusterChange}
              className={"mt-1"}
              minWidth={"100%"}
              defaultValue={allClusterOptions.find(option => option.value === cluster)}
            />
        </Col>
      </Row>
    </VsiBubble>
  );
};

export default function CloudResourceModeling({ title }) {
  const { theme, client, periods, periodId, user, getAccessTokenSilently } = useVsiAppContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const [allResourceGroups, { isLoading: isAllResourceGroupsLoading }] =
    useDataWithRoute(
      `/api/v6/report/cloud_all_resource_groups_proc?client_id=${client.clientId}&period_id=${periodId}`
    );

  const [allDates, setAllDates] = useState([]);
  const [dateSelected, setDateSelected] = useState(null);

  const [chartType, setChartType] = useState(
    searchParams.get("chart_type") || "cost"
  );
  const [chartConfig, setChartConfig] = useState(
    allTypes.find((type) => type.name === chartType).chartConfig
  );
  const [error, setError] = useState(null);

  const [allGroups, setAllGroups] = useState(
    allResourceGroups?.recordset?.map?.((group) => ({
      value: group.resource_group,
      label: group.resource_group,
    })) || []
  );

  const resourceGroup = searchParams.get("resource_group");
  let activeGroup = allGroups?.[0] ?? null;

  if (resourceGroup) {
    activeGroup =
      allGroups.find((group) => group.value === decodeURI(resourceGroup)) ??
      activeGroup;
  }

  const [selectedGroup, setSelectedGroup] = useState(activeGroup);
  const [projectName, setProjectName] = useState("");
  const [projectNotes, setProjectNotes] = useState("");

  const [activeCapacityPlan, setActiveCapacityPlan] = useState({
    isBudgetForecasted: false,
  }); // would need to do a get from mongo
  const [budgets, setBudgets] = useState({});
  const [resourceCardData, setResourceCardData] = useState({});
  const [chartTrendData, setChartTrendData] = useState([]);

  let savedPlanRoute = `/api/v6/admin/resource_group_capacity_model/${
    client.clientId
  }/${encodeURI(selectedGroup?.value)}`;
  const [
    savedPlanData,
    { reFetchData: reFetchSavedPlanData, setData: setSavedPlanData },
  ] = useAuthWithRoute(savedPlanRoute, Boolean(selectedGroup?.value));

  let historyRoute = `/api/v6/report/cloud_history_modeling_by_resource_group_proc?client_id=${
    client.clientId
  }&period_id=${periodId}&resource_group=${encodeURIComponent(selectedGroup?.value)}`;
  const [resourceGroupDetails, { isLoading: isResourceGroupDetailsLoading }] = useDataWithRoute(historyRoute, Boolean(selectedGroup?.value));

  const virtualPeriodId = periods?.find(a => a.context?.toUpperCase() === "VIRTUAL")?.period_id;
  const clustersRoute = `/api/v6/report/enterprise_virtual_all_clusters_proc?client_id=${client.clientId}&period_id=${virtualPeriodId}`;
  const [{ recordset: allClusters}, {isLoading: isClustersLoading }] = useDataWithRoute(clustersRoute);

  const [allClusterOptions, setAllClusterOptions] = useState([]);

  const [params, setParams] = useUrlState({
    cluster: '',
  });

  const { cluster } = params;
  const foundCluster = allClusterOptions.find(option => option.value === cluster) ?? {};
  const clusterCostRoute = `/api/v6/report/virtual_cluster_cost_proc?client_id=${client.clientId}&period_id=${virtualPeriodId}&vcenter_name=${encodeURIComponent(foundCluster.vcenter)}&cluster_name=${encodeURIComponent(cluster)}`;
  const [{ recordset: clusterCostTrend }, { isLoading: isClusterCostLoading }] = useDataWithRoute(clusterCostRoute, Boolean(cluster && foundCluster.vcenter && !isClustersLoading));

  useTitleFromComponent(`${activeGroup?.label || "..."} - ${title}`);

  async function calculateAvgFromCloud() {
    const cloudArr = resourceGroupDetails?.recordsets?.[0];
    const details = cloudArr?.[cloudArr?.length - 1] ?? {};
    const numVms = details?.total_vms || 1;

    const cloudData = [
      {
        average_vm_cpu_size: Math.ceil(details?.vcpu / numVms) || 0,
        average_vm_memory_size: Math.ceil(details?.total_memory / numVms) || 0,
        average_vm_disk_size: Math.round(details?.total_disk / numVms) || 0,
        vm_avg_cost: details?.total_cost / numVms || 0,
      },
    ];

    setResourceCardData(cloudData);
  }

  const handleCapacityPlan = (data) => {
    const capacity = {
      cpu: 0,
      mem: 0,
      disk: 0,
      vm: 0,
      cost: 0,
    };

    const workload = { ...capacity };

    let budgetDiff = 0;
    return (data || []).map((datum) => {
      const newDatum = { ...datum };
      const dateToCompare = dayjs(datum.collection_date).format("MM/YYYY");
      let budget = datum.budget;
      if (
        typeof activeCapacityPlan?.[dateToCompare]?.customWorkload === "object"
      ) {
        // do something
        const { customWorkload } = activeCapacityPlan[dateToCompare];

        workload.cpu = workload.cpu + customWorkload.cpu;
        workload.mem = workload.mem + customWorkload.mem;
        workload.disk = workload.disk + customWorkload.disk;
        workload.vm = workload.vm + customWorkload.vm;
        workload.cost = workload.cost + (customWorkload.cost ?? 0);
      }

      if (activeCapacityPlan?.[dateToCompare]?.budget) {
        budget = parseFloat(activeCapacityPlan?.[dateToCompare]?.budget);
        budgetDiff = budget - datum.budget;
      }

      //workload
      newDatum.cpu_used = newDatum.cpu_used + workload.cpu;
      newDatum.memory_used = newDatum.memory_used + workload.mem;
      newDatum.disk_used = newDatum.disk_used + workload.disk;
      newDatum.total_vms = newDatum.total_vms + workload.vm;
      newDatum.total_cost = newDatum.total_cost + workload.cost;
      newDatum.budget = newDatum.budget + budgetDiff;
      return newDatum;
    });
  };

  const handleBudgetPlan = (data) => {
    return (data || []).map((datum) => {
      const newDatum = { ...datum };
      const dateToCompare = dayjs(datum.collection_date).format("MM/YYYY");
      if (activeCapacityPlan?.[dateToCompare]?.budget) {
        newDatum.budget = parseFloat(
          activeCapacityPlan?.[dateToCompare]?.budget
        );
      }
      return newDatum;
    });
  };

  const handleDataWithModel = (data) => {
    let allFieldsForForecast = [];
    allTypes.forEach((type) => {
      type.chartDefs.forEach((def) => {
        if (def.field) allFieldsForForecast.push(def.field);
      });
    });

    const chartTypeFound = allTypes.find((type) => type.name === chartType) || [];

    const budgetedData = handleBudgetPlan(data);

    if (!activeCapacityPlan?.isBudgetForecasted) {
      allFieldsForForecast = allFieldsForForecast.filter(
        (field) => field !== "budget"
      );
    }

    const newData = [
      ...budgetedData,
      ...forecastWithRateV2(
        budgetedData,
        allFieldsForForecast,
        12
      )
    ];

    const withPlanData = handleCapacityPlan(newData);

    const avgOnPremCost = Math.round(
      (withPlanData.reduce((a, b) => a + b.total_cost, 0) /
        withPlanData.length) *
        1.5
    );

    let readyForChartData = [];

    if (clusterCostTrend.length) {
      const dates = {};
      let avgCost = 0;
      clusterCostTrend.forEach(datum => {
        const date = dayjs(datum.collection_date).format("MM/YYYY");
        const onPremCost = datum.total_cost;
        avgCost += onPremCost;
        dates[date] = {
          ...datum,
          on_prem_cost: onPremCost,
        };
      });

      let runningCost = 0;

      readyForChartData = withPlanData.map((plan) => {
        const thisDateCost = dates?.[dayjs(plan.collection_date).format("MM/YYYY")]?.on_prem_cost;
        if (!runningCost) {
          runningCost = thisDateCost ?? avgCost / clusterCostTrend.length;
        }
        if (thisDateCost && thisDateCost !== runningCost) {
          runningCost = thisDateCost;
        }

        return {
          ...plan,
          on_prem_cost: runningCost,
        }
      });

    } else {
      readyForChartData = withPlanData.map((plan) => ({
        ...plan,
        on_prem_cost: avgOnPremCost,
      }));
    }


    const formattedData = formatStockChartConfig(
      readyForChartData,
      chartTypeFound?.chartDefs || []
    );

    setChartTrendData(
      formattedData.series.map((series) => ({
        ...series,
        zoneAxis: "x",
        zones: [
          {
            value: Date.parse(
              data?.[data.length - 1]?.collection_date?.split?.("T")?.[0]
            ),
          },
          {
            dashStyle: "Dash",
          },
        ],
      }))
    );
    calculateAvgFromCloud();
    setChartConfig(chartTypeFound.chartConfig);
  };

  const formatSavedData = (data) => {
    const savedPlan = data?.[0];
    const activePlan = {
      isBudgetForecasted: false,
    };
    const activeBudgets = {};

    if (savedPlan?._id) {
      (savedPlan?.adjustments ?? []).forEach((adjustment) => {
        if (!activePlan[adjustment.actualDate]) {
          activePlan[adjustment.actualDate] = {};
        }
        activePlan[adjustment.actualDate] = { ...adjustment };
        if (adjustment.budget) {
          activeBudgets[adjustment.actualDate] = adjustment.budget;
        }
      });
      activePlan.isBudgetForecasted = savedPlan.isBudgetForecasted;
    }

    setActiveCapacityPlan(activePlan);
    setProjectName(savedPlan?.projectName ?? "");
    setProjectNotes(savedPlan?.notes ?? "");
    if (Object.keys(activeBudgets).length > 0) {
      setBudgets(activeBudgets);
    }
  };

  useEffect(() => {
    setAllClusterOptions(allClusters.map(({ cluster_name, vcenter_name }) => ({
      value: cluster_name,
      label: cluster_name,
      vcenter: vcenter_name,
    })));
  }, [allClusters]);

  useEffect(() => {
    formatSavedData(savedPlanData);
  }, [savedPlanData, resourceGroupDetails?.recordsets]);

  useEffect(() => {
    if (allGroups.length < 1 && allResourceGroups?.recordset?.length) {
      const allGroupsNew =
        allResourceGroups?.recordset?.map?.((group) => ({
          value: group.resource_group,
          label: group.resource_group,
        })) ?? [];

      const searchedParam = decodeURIComponent(searchParams.get("resource_group"));

      const groupToSet = allGroupsNew.find(group => group.value === searchedParam) || allGroupsNew?.[0] || null;
      setAllGroups(allGroupsNew);
      setSelectedGroup(groupToSet);
    }
  }, [allResourceGroups, searchParams]);

  useEffect(() => {
    handleDataWithModel(resourceGroupDetails?.recordsets?.[0] ?? []);
  }, [resourceGroupDetails, chartType, activeCapacityPlan, clusterCostTrend]);

  useEffect(() => {
    const data = resourceGroupDetails?.recordsets?.[0];
    if (allDates.length < 1 || data?.length > 0) {
      const latestDate = Date.parse(
        data?.[data?.length - 1]?.collection_date?.split?.("T")?.[0]
      );
      const dates = data?.length > 0 ? returnDates(latestDate) : [];
      setAllDates(dates);
      setDateSelected(dates?.[0] ?? {});
    }
  }, [resourceGroupDetails?.recordsets]);

  const changeDataWithCustomResource = (customInfo) => {
    const activePlan = {
      ...activeCapacityPlan,
    };
    activePlan[dateSelected.value] = {
      ...(activePlan?.[dateSelected.value] || {}),
      customWorkload: {
        ...(activePlan?.[dateSelected.value]?.customWorkload ?? {}),
        ...customInfo,
      },
    };

    setActiveCapacityPlan(activePlan);
  };

  const changeDataWithBudget = (budget) => {
    const activeBudgets = {
      ...budgets,
    };

    const activePlan = {
      ...activeCapacityPlan,
    };

    activeBudgets[dateSelected.value] = budget;
    activePlan[dateSelected.value] = {
      ...(activePlan[dateSelected.value] || {}),
      budget,
    };

    setBudgets(activeBudgets);
    setActiveCapacityPlan(activePlan);
  };

  const changeDataWithIsBudgetForecasted = () => {
    const activePlan = {
      ...activeCapacityPlan,
      isBudgetForecasted: !activeCapacityPlan.isBudgetForecasted,
    };

    setActiveCapacityPlan(activePlan);
  };

  const onUnitChange = (selectedUnit) => {
    setSelectedGroup(selectedUnit);
    searchParams.set("resource_group", encodeURIComponent(selectedUnit.value));
    setSearchParams(searchParams, { replace: true });
  };

  const handleDateChange = (date) => {
    setDateSelected(date);
  };

  const onChartTypeChange = (type) => {
    setChartType(type.name);
    searchParams.set("chart_type", type.name);
    setSearchParams(searchParams, { replace: true });
  };

  const onDeleteClick = async () => {
    const planId = savedPlanData?.[0]?._id;
    if (planId) {
      try {
        const token = await getAccessTokenSilently();
        await DeleteAuth(
          `/api/v6/admin/resource_group_capacity_model/${planId}`,
          token
        );
        setActiveCapacityPlan({
          isBudgetForecasted: false,
        });
        setSavedPlanData([]);
        setProjectNotes("");
        setProjectName("");
      } catch (error) {
        setError(error);
        console.error(error);
      }
    }
  };
  const onUndoClick = () => {
    formatSavedData(savedPlanData);
  };

  const onFormSubmit = async () => {
    const adjustments = [];

    Object.keys(activeCapacityPlan).forEach((planKey) => {
      const adjustment = {
        ...activeCapacityPlan[planKey],
        actualDate: planKey,
      };
      if (planKey !== "isBudgetForecasted") {
        if (budgets[planKey]) {
          adjustment.budget = budgets[planKey];
        }
        adjustments.push(adjustment);
      }
    });

    const planData = {
      resourceGroup: activeGroup.label,
      clientId: client.clientId,
      notes: projectNotes,
      projectName,
      adjustments,
      isBudgetForecasted: activeCapacityPlan.isBudgetForecasted,
    };

    try {
      const planId = savedPlanData?.[0]?._id;
      const token = await getAccessTokenSilently();
      if (planId) {
        await PutAuth(
          `/api/v6/admin/resource_group_capacity_model/${planId}`,
          planData,
          token
        );
        reFetchSavedPlanData();
      } else {
        await PostAuth(
          `/api/v6/admin/resource_group_capacity_model`,
          planData,
          token
        );
        reFetchSavedPlanData();
      }
    } catch (error) {
      setError(error);
      console.error(error);
    }
  };

  const onClusterChange = ({ value }) => {
    setParams({
      cluster: value
    });
  };

  return (
    <Card className={`report-card p-2 nav-card-${theme}`}>
      <VsiH4>
        Cloud Resource Group Modeling - {selectedGroup?.label ?? "N/A"}
      </VsiH4>

      <Row>
        <Col xs={12} md={3} className={"mb-2"}>
          <VsiBubble noAutobot>
            <VsiLabeledSelect
              isDisabled={
                isAllResourceGroupsLoading || isResourceGroupDetailsLoading
              }
              isLoading={
                isAllResourceGroupsLoading || isResourceGroupDetailsLoading
              }
              options={allGroups}
              label={"Resource Group:"}
              onChange={onUnitChange}
              minWidth={"100%"}
              defaultValue={selectedGroup}
            />
            <VsiLabeledSelect
              options={allDates}
              isDisabled={
                isAllResourceGroupsLoading || isResourceGroupDetailsLoading
              }
              label={"Month to Adjust:"}
              minWidth={"100%"}
              onChange={handleDateChange}
              defaultValue={dateSelected}
            />
            <Row>
              <Col xs={12}>
                <VsiLgText textAlign={"left"}>Project Name:</VsiLgText>
                <Input
                  value={projectName}
                  onChange={({ target }) => setProjectName(target.value)}
                  color={theme}
                />
              </Col>
              <Col xs={12} className={"mt-1"}>
                <VsiLgText textAlign={"left"}>Notes:</VsiLgText>
                <Input
                  name="text"
                  invalid={!!error}
                  value={projectNotes}
                  onChange={({ target }) => setProjectNotes(target.value)}
                  type="textarea"
                />
                <FormFeedback>
                  {error?.response?.data?.message ?? error?.message}
                </FormFeedback>

                <VsiHr />
              </Col>
              <Col className={"mt-3 d-flex justify-content-between"}>
                <ButtonGroup>
                  <Button
                    color={"danger"}
                    id={"clear-btn-plan"}
                    onClick={onDeleteClick}
                  >
                    <i className={"fa-solid fa-trash"} />
                  </Button>
                  <Button
                    color={"info"}
                    id={"reset-btn-plan"}
                    onClick={onUndoClick}
                  >
                    <i className={"fa-solid fa-rotate-left"} />
                  </Button>
                </ButtonGroup>
                <UncontrolledTooltip
                  placement={"bottom"}
                  target={"clear-btn-plan"}
                >
                  Clear saved adjustments
                </UncontrolledTooltip>
                <UncontrolledTooltip
                  placement={"bottom"}
                  target={"reset-btn-plan"}
                >
                  Undo local adjustments
                </UncontrolledTooltip>
                <ButtonGroup>
                  <Button
                    color={"success"}
                    id="save-btn-plan"
                    onClick={onFormSubmit}
                  >
                    <i className={"fa-solid fa-floppy-disk"} />
                  </Button>
                </ButtonGroup>

                <UncontrolledTooltip
                  placement={"bottom"}
                  target={"save-btn-plan"}
                >
                  Save adjustments
                </UncontrolledTooltip>
              </Col>
            </Row>
          </VsiBubble>
          <VsiHr />
          <BudgetCard
            isBudgetForecasted={activeCapacityPlan?.isBudgetForecasted}
            changeIsBudgetForecasted={changeDataWithIsBudgetForecasted}
            budget={budgets?.[dateSelected?.value]}
            changeBudget={changeDataWithBudget}
            allClusterOptions={allClusterOptions}
            cluster={cluster}
            onClusterChange={onClusterChange}
            isClustersLoading={isClustersLoading}
          />

          <VsiHr />
          <CustomResourceCard
            resourceType={"workload"}
            modelingContext={"cloud"}
            changeDataWithCustomResource={changeDataWithCustomResource}
            workloadMetrics={resourceCardData[0] ?? {}}
            activeCustomPlan={
              activeCapacityPlan?.[dateSelected?.value]?.[`customWorkload`] ?? {}
            }
          />
        </Col>
        <Col xs={12} md={9}>
          <Row>
            <Col xs={12}>
              <VsiBubble noAutobot>
                <div className={"d-flex flex-row justify-content-end"}>
                  <ButtonGroup>
                    {allTypes.map((type, index) => (
                      <Button
                        disabled={chartType === type.name}
                        key={`chart-btn-${index}-${type.name}`}
                        onClick={() => onChartTypeChange(type)}
                      >
                        {type.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>
                <Stockchart series={chartTrendData}
                            config={chartConfig}
                            formatLabels={chartType === "cost" ? "${value:,f}" : null}
                            formatData={chartType === "cost" ? "${point.y:,.2f}" : null}
                            formatPoint={chartType === "cost" ? "{series.name}: <b>${point.y}</b>" : null}/>
              </VsiBubble>
            </Col>
            <Col xs={12} className={"mt-2"}>
              <VsiBubble unit={selectedGroup?.value}>
                <Grid
                  data={resourceGroupDetails?.recordsets?.[1] ?? []}
                  columnDefs={cloudResourceModelingDefs}
                />
              </VsiBubble>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );
}
